import {Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {AppEnities, CasiCloseOptions, FormModes} from '../../common/constants.lib';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CheckBoxModule, InputsModule} from '@progress/kendo-angular-inputs';
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {DialogService, DialogsModule, WindowModule} from '@progress/kendo-angular-dialog';
import {NotificationModule, NotificationService} from '@progress/kendo-angular-notification';
import {KENDO_PAGER, PagerModule} from '@progress/kendo-angular-pager';
import {ExcelModule, GridComponent, GridModule, PagerPosition, PagerType} from '@progress/kendo-angular-grid';
import {MulticheckFilterComponent} from '../../filters/multicheck-filter/multicheck-filter.component';
import {Subscription} from 'rxjs';
import {LoggerService} from '../../shared/logger.service';
import {MeService} from '../../auth/me.service';
import {ToastrService} from 'ngx-toastr';
import {
  CasiDateRange,
  Evaluation,
  GridResponse,
  IUnitOfStudy,
  Organisation,
  PlacementReviewStudent
} from '../../models/models.lib';
import {EvaluationService} from '../../evaluation/evaluation.service';
import {AutoFocusDirective} from '../../directives/auto-focus.directive';
import {OrganisationService} from '../../organisation/organisation.service';
import {DropDownsModule} from '@progress/kendo-angular-dropdowns';
import {DateInputsModule, FormatSettings} from '@progress/kendo-angular-dateinputs';
import moment from 'moment';
import {PlacementReviewService} from '../../placement-review/placement-review.service';
import {ResearchDataExportService} from '../research-data-export.service';
const { RESEARCH_DATA_EXPORT } = AppEnities;
import testData from '../testData.json';

@Component({
  selector: 'casi-research-data-export.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    AutoFocusDirective,
    DropDownsModule,
    DateInputsModule,
    ExcelModule
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './research-data-export.component.html',
  styleUrl: './research-data-export.component.scss'
})
export class ResearchDataExportComponent implements OnInit, OnDestroy {

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;
  @ViewChild(GridComponent) grid!: GridComponent;

  private _subs: Subscription[] = [];
  public canView: boolean = false;

  searchForm: FormGroup;
  organisationList: Organisation[] = [];
  evaluations: Evaluation[] = [];
  unitOfStudyList: IUnitOfStudy[] = [];
  allUnitOfStudyList: IUnitOfStudy[] = [];
  canPrint = false;

  public dateRange: CasiDateRange = new CasiDateRange(null, null);
  public format: FormatSettings = {
    displayFormat: "dd/MM/yyyy",
    inputFormat: "dd/MM/yy",
  };

  contents = [
    {text: "Student Start Review", value: "student_start_assessment_id"},
    {text: "Student Mid Review", value: "student_mid_assessment_id"},
    {text: "Student End Review", value: "student_end_assessment_id"},
    {text: "Supervisor Mid Review", value: "supervisor_mid_assessment_id"},
    {text: "Supervisor End Review", value: "supervisor_end_assessment_id"}
  ];


  private rowData!: GridResponse;
  public gridData: any[] = [];
  public dataMap: any[] = [];

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = 10;
  public fileName: string = "Research Data Export.xlsx";

  constructor(
    private logger: LoggerService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private me: MeService,
    private evaluationService: EvaluationService,
    private organisationService: OrganisationService,
    private toastr: ToastrService,
    private placementReviewService:PlacementReviewService,
    private dataService: ResearchDataExportService
  ) {
    this.searchForm = this.fb.group(
      {
        organisations: [null],
        evaluation_id: [null, [Validators.required]],
        content_id: [null],
        start: [null],
        end: [null],
        units: [null]
      }
    );
  }

//#region Form

  get f() {return this.searchForm.controls;}
  get organisations() {return this.searchForm.get('organisations');}
  get evaluation_id() {return this.searchForm.get('evaluation_id');}
  get content_id() {return this.searchForm.get('content_id');}
  get start() {return this.searchForm.get('start');}
  get end() {return this.searchForm.get('end');}
  get units() {return this.searchForm.get('units');}
  //#endregion

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_view } = this.me.allICanDo(RESEARCH_DATA_EXPORT);
    this.canView = can_view;
    this.loadEvaluations();
    this.loadOrganisations();
    this.loadUnits();

    // this.organisations?.valueChanges.subscribe(value => {
    //   this.filterUnits(value);
    // });

    this.evaluation_id?.valueChanges.subscribe(value => {
        this.filterUnits(value);
      });

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      if (!e)
        return;

     this.rowData = e;// testData
      this.loadData();
      this.canPrint = true;
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  public onSearchAction(): void {
    this.logger.debug(`onSearchAction...`, this.searchForm.value);
    this.canPrint = false;
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }

    const { start, end } = this.dateRange;
    this.searchForm.patchValue({ placement_start_date: start ? moment(start).format("YYYY-MM-DD") :  null });
    this.searchForm.patchValue({ placement_end_date: start ? moment(end).format("YYYY-MM-DD") :  null });

    this.logger.debug(`onSearchAction...`, this.searchForm.value);
    this.dataService.query(this.searchForm.value);
  }

  public onExportAction(): void {
    this.logger.debug(`onExportAction...`);
    const id = this.evaluation_id?.value;
    const evaluation = this.evaluations.find(e => e.id === id);
    this.fileName = evaluation ? `Research Data Export - ${evaluation.evaluation_name}.xlsx` :  this.fileName ;
    this.grid.saveAsExcel();
  }

  private loadEvaluations(): void {
    this.logger.debug('loadEvaluations...');
    this.evaluationService.get().subscribe({
      next: data => this.evaluations = data || [],
      error: err => console.error('Failed to load evaluations', err)
    });
  }

  private loadOrganisations(): void {
    this.organisationService.get().subscribe({
      next: data => this.organisationList = data || [],
      error: err => console.error('Failed to load organisations', err)
    });

  }

  private loadUnits(): void {
    this.placementReviewService.getUnitsOfStudy()
      .then(data => {
        this.unitOfStudyList = [];
        this.allUnitOfStudyList = data || [];
        // this.unitOfStudyList = [...this.allUnitOfStudyList];
      })
      .catch(e => console.error('Failed to load units', e))
  }

  // private loadUnits(): void {
  //   this.placementReviewService.getUnitsOfStudy()
  //     .then(data => {
  //       this.unitOfStudyList = data || [];
  //       this.allUnitOfStudyList = data || [];
  //       this.unitOfStudyList = [...this.allUnitOfStudyList];
  //     })
  //     .catch(e => console.error('Failed to load units', e))
  // }

  private filterUnits(selectedEvaluation: number | null): void {
    if (!selectedEvaluation) {
      // this.units = [];
      this.unitOfStudyList = [];
      return;
    }

    this.unitOfStudyList = this.allUnitOfStudyList.filter(unit => selectedEvaluation == unit.evaluation_id!);

    // Elimina eventuali unità già selezionate che non appartengono più
    const selectedUnits: number[] = this.units?.value || [];

    const validUnits = selectedUnits.filter(id =>
      this.unitOfStudyList.some(u => u.id === id)
    );

    this.units?.setValue(validUnits);
  }
  // private filterUnits(selectedOrganisations: number[] | null): void {
  //
  //   if (!selectedOrganisations || selectedOrganisations.length === 0) {
  //     this.unitOfStudyList = [...this.allUnitOfStudyList];
  //     return;
  //   }
  //
  //   this.unitOfStudyList = this.allUnitOfStudyList.filter(unit => selectedOrganisations.includes(unit.organisation_id!));
  //
  //   // Elimina eventuali unità già selezionate che non appartengono più
  //   const selectedUnits: number[] = this.units?.value || [];
  //
  //   const validUnits = selectedUnits.filter(id =>
  //     this.unitOfStudyList.some(u => u.id === id)
  //   );
  //
  //   this.units?.setValue(validUnits);
  // }

  private loadData(): void {
    if (!this.rowData.header || !this.rowData.body) {
      return;
    }

    this.dataMap = [...this.rowData.header].sort(
      (a, b) => a.order_no - b.order_no
    );

    this.gridData = this.rowData.body.map(row => {
      const obj: Record<string, any> = {};

      row.forEach(item => {
        const field = Object.keys(item)[0];
        obj[field] = item[field];
      });

      return obj;
    });

    this.pageSize = this.gridData.length;
  }

  // private loadData(): void {
  //   if (!this.rowData.header || !this.rowData.body)
  //     return;
  //
  //   this.dataMap = this.rowData.header.sort((a, b) => a.order_no - b.order_no);
  //
  //   const fields = this.rowData.header
  //     .sort((a, b) => a.order_no - b.order_no)
  //     .map(h => h.field_name);
  //
  //   this.gridData = this.rowData.body.map(row => {
  //     const obj: Record<string, any> = {};
  //
  //     fields.forEach(field => {
  //       obj[field] = row[field];
  //     });
  //
  //     return obj;
  //   });
  //
  //   this.pageSize = this.gridData.length;
  // }
}
